import React, { useEffect, useMemo, useRef } from "react";
import { makeStyles, useTheme, Button } from "@seahorse/ui";
import type { FC } from "react";
import { FaCopy as _FaCopy, FaDownload as _FaDownload, FaPen as _FaPen, FaRedo as _FaRedo } from "react-icons/fa";

import html2pdf from "html2pdf.js";
const FaCopy = _FaCopy as unknown as FC<any>;
const FaDownload = _FaDownload as unknown as FC<any>;
const FaPen = _FaPen as unknown as FC<any>;
const FaRedo = _FaRedo as unknown as FC<any>;

interface CoHedgerSummaryTileComponentProps {
  loading: any;
  summary: any;

  currencyPair: string;
  assetType: string;
  executionType: string;
  expirationDate?: string | null;
}

function mdToHtml(md: string): string {
  return md
    .replace(/<h1(\b[^>]*)>/gi, "<h3$1>")
    .replace(/<\/h1>/gi, "</h3>")
    .replace(/<h2(\b[^>]*)>/gi, "<h4$1>")
    .replace(/<\/h2>/gi, "</h4>")
    .replace(/<h3(\b[^>]*)>/gi, "<h5$1>")
    .replace(/<\/h3>/gi, "</h5>");
}

function fmtDate(x?: string | null) {
  if (!x) return "—";
  const d = new Date(x);
  if (Number.isNaN(d.getTime())) return x;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

const CoHedgerSummaryTileComponent: React.FunctionComponent<
  CoHedgerSummaryTileComponentProps
> = ({
  summary,
  loading,
  currencyPair,
  assetType,
  executionType,
  expirationDate,
}) => {
  const theme = useTheme();

  const colors = useMemo(
    () => ({
      Dark: theme.globallink.colors.Grey[1100],
      Light: theme.globallink.colors.White[500],
      border: theme.globallink.colors.White[500],
      title: theme.globallink.colors.White[500],
      sub: theme.globallink.colors.Grey[200],
    }),
    [theme]
  );

  const useStyles = makeStyles({
    container: {
      width: "100%",
      height: "100%",
      display: "flex",
      marginTop: "10px",
      marginBottom: "10px",
      flexDirection: "column",
      borderRadius: "8px",
      padding: "10px",
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.Dark,
    },
    headerRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      paddingBottom: "10px",
    },
    title: {
      color: colors.title,
      fontWeight: 700,
      fontSize: "14px",
      margin: 0,
    },
    body: {
      overflow: "auto",
      height: "100%",
      padding: "10px",
    },
    card: {
      backgroundColor: colors.Light,
      color: "#111",
      borderRadius: "6px",
      padding: "14px",
    },
    summaryHtml: {
      fontSize: "13px",
      lineHeight: 1.55,
    },

    pdfHeader: {
      borderBottom: "1px solid #e6e6e6",
      paddingBottom: "10px",
      marginBottom: "12px",
    },

    pdfTitle: {
      fontSize: "16px",
      fontWeight: 800,
      marginBottom: "6px",
    },

    pdfMetaGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, 1fr)",
      gap: "6px 14px",
      fontSize: "12px",
    },

    actionsRow: {
      display: "flex",
      gap: "10px",
      justifyContent: "flex-end",
      marginTop: "12px",
    },
  });

  const classes = useStyles();

  const summaryHtml = useMemo(() => mdToHtml(summary || ""), [summary]);

  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(summary || "");
    alert("Copied");
  };

  // ✅ PDF Download
  const downloadPdf = () => {
    const element = document.getElementById("cohedger-report-pdf");
    if (!element) return;

    const fileName = `CoHedger_${currencyPair}_${new Date()
      .toISOString()
      .slice(0, 10)}.pdf`;

    html2pdf()
      .set({
        margin: 10,
        filename: fileName,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      })
      .from(element)
      .save();
  };

  return (
    <div className={classes.container}>
      <div className={classes.headerRow}>
        <p className={classes.title}>Hedging Analysis Report</p>

        <div>
          <FaCopy
            style={{ cursor: "pointer", marginRight: "12px" }}
            onClick={copyToClipboard}
          />
          <FaDownload
            style={{ cursor: "pointer" }}
            onClick={downloadPdf}
          />
        </div>
      </div>

      <div className={classes.body}>
        <div className={classes.card}>
          {/* ✅ PDF CONTENT */}
          <div id="cohedger-report-pdf">
            <div className={classes.pdfHeader}>
              <div className={classes.pdfTitle}>
                Hedging Analysis Report
              </div>

              <div className={classes.pdfMetaGrid}>
                <div><b>Currency Pair:</b> {currencyPair || "—"}</div>
                <div><b>Asset Type:</b> {assetType || "—"}</div>
                <div><b>Execution Type:</b> {executionType || "—"}</div>
                <div><b>Expiration Date:</b> {fmtDate(expirationDate)}</div>
              </div>
            </div>

            <div
              className={classes.summaryHtml}
              dangerouslySetInnerHTML={{ __html: summaryHtml }}
            />
          </div>

          <div className={classes.actionsRow}>
            <Button disabled={loading || !summary} onClick={downloadPdf}>
              Download PDF
            </Button>
            <Button onClick={copyToClipboard}>Copy</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoHedgerSummaryTileComponent;
